import React, { useState, useEffect, useCallback } from 'react';
// import { useDispatch } from 'react-redux';
import { Grid, Header, Button, Message, Modal } from 'semantic-ui-react';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { useSharedData } from '../../hooks/core/useZoneHooks';
import { BooksListEnh } from '../lists';
import { BookFormEnh } from '../forms';

/**
 * BookManagement-enh component
 * Enhanced version of BookManagement with improved UI matching BooksManagement-bcw
 * Manages books for a selected literary group
 * @returns {JSX.Element} BookManagement-enh component
 */
const BookManagementEnh = () => {
  const sharedData = useSharedData();
  // const dispatch = useDispatch();

  // Extract data from new hook
  const {
    litGroups,
    books,
    selections
  } = sharedData;

  // Map to old interface for compatibility
  const litGroupsArray = Array.isArray(litGroups) ? litGroups : [];
  const booksArray = Array.isArray(books) ? books : [];
  const selectionsObj = selections || {};

  const selectedLitGroup = litGroupsArray.find(lg => lg.uuid === selectionsObj.litGroupId) || null;
  const selectedBook = booksArray.find(b => b.uuid === selectionsObj.bookId) || null;
  const litGroupId = selectionsObj.litGroupId;
  const bookId = selectionsObj.bookId;

  // Map actions
  const setBookSelection = sharedData.actions.setBookSelection;
  const setLitGroupSelection = sharedData.actions.setLitGroupSelection;
  const fetchBooks = sharedData.actions.fetchBooks;
  const fetchLitGroups = sharedData.actions.fetchLitGroups;

  // Create dummy functions for missing functionality
  // const clearBookSelection = () => setBookSelection(null);
  const clearBookSelection = useCallback(() => setBookSelection(null), [setBookSelection]);

  // const fetchLitGroup = () => Promise.resolve();
  // const fetchLitGroupProfile = () => Promise.resolve();

  // State for editing mode
  const [isEditing, setIsEditing] = useState(false);

  // State for delete confirmation modals
  const [deleteLitGroupModalOpen, setDeleteLitGroupModalOpen] = useState(false);
  const [litGroupToDelete, setLitGroupToDelete] = useState(null);
  const [deleteBookModalOpen, setDeleteBookModalOpen] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);

  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage] = useState('');

  // Set editing mode when a book is selected
  useEffect(() => {
    setIsEditing(!!bookId);
  }, [bookId]);

  // Handler for selecting a literary group
  // const handleSelectLitGroup = useCallback((litGroup) => {
  //   // Set the selected literary group - this already fetches LitGroup data and profile
  //   setLitGroupSelection(litGroup.uuid);

  //   // Fetch books for this literary group - force a fresh fetch
  //   fetchBooks(litGroup.uuid, true);

  //   // Clear any selected book
  //   clearBookSelection();
  //   setIsEditing(false);
  // }, [setLitGroupSelection, fetchBooks, clearBookSelection]);

  // Handler for deleting a literary group
  // const handleDeleteLitGroup = useCallback((litGroup) => {
  //   // Show the delete confirmation modal
  //   setLitGroupToDelete(litGroup);
  //   setDeleteLitGroupModalOpen(true);
  // }, []);

  // Handler for confirming literary group deletion
  const confirmDeleteLitGroup = useCallback(() => {
    // TODO: Implement literary group deletion API call
    console.log('Deleting literary group:', litGroupToDelete);

    // Close the modal
    setDeleteLitGroupModalOpen(false);
    setLitGroupToDelete(null);

    // If the deleted group was selected, clear the selection
    if (selectedLitGroup && selectedLitGroup.uuid === litGroupToDelete.uuid) {
      setLitGroupSelection(null);
      clearBookSelection();
      setIsEditing(false);
    }

    // Refresh the literary groups list
    fetchLitGroups();
  }, [litGroupToDelete, selectedLitGroup, setLitGroupSelection, clearBookSelection, fetchLitGroups]);

  // Handler for selecting a book
  const handleBookSelect = useCallback((book) => {
    if (book && book.uuid) {
      // Set the selection first
      setBookSelection(book.uuid);
      // Then fetch the book data directly using the thunk
      // dispatch(fetchBook(book.uuid));
      setIsEditing(true);
    }
  }, [setBookSelection]);

  // Handler for deleting a book
  const handleDeleteBook = useCallback((book) => {
    // Show the delete confirmation modal
    setBookToDelete(book);
    setDeleteBookModalOpen(true);
  }, []);

  // Handler for confirming book deletion
  const confirmDeleteBook = useCallback(() => {
    // TODO: Implement book deletion API call
    console.log('Deleting book:', bookToDelete);

    // Close the modal
    setDeleteBookModalOpen(false);
    setBookToDelete(null);

    // If the deleted book was selected, clear the selection
    if (selectedBook && selectedBook.uuid === bookToDelete.uuid) {
      clearBookSelection();
      setIsEditing(false);
    }

    // Refresh the books list
    if (litGroupId) {
      fetchBooks(litGroupId);
    }
  }, [bookToDelete, selectedBook, clearBookSelection, litGroupId, fetchBooks]);

  // Handle save
  const handleSaveBook = useCallback((updatedBook) => {
    // Refresh data after save
    if (updatedBook && updatedBook.uuid) {
      // Set the selection first
      setBookSelection(updatedBook.uuid);
      setIsEditing(true);

      // Refresh the books list
      if (litGroupId) {
        fetchBooks(litGroupId);
      }

      setSuccessMessage('Book saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [setBookSelection, litGroupId, fetchBooks]);

  return (
    <ManagementHeader
      title="Book Management"
      error={errorMessage}
      isLoading={false}
      width="1024px"
      isEditing={isEditing}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <div>
              {/* Only show BooksList when a Literary Group is selected */}
              {selectedLitGroup ? (
                <BooksListEnh
                  onBookSelect={handleBookSelect}
                  onDeleteBook={handleDeleteBook}
                />
              ) : (
                <div className="bw-empty-selection-message marginTop_10">
                  <Message color='blue'>Select a Literary Group to view its books.</Message>
                </div>
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={10}>
            {selectedLitGroup && (
              <>
                {selectedBook ? (
                  // Only render BookFormEnh when a book is selected
                  <>
                    {successMessage && (
                      <Message positive>
                        <Message.Header>Success</Message.Header>
                        <p>{successMessage}</p>
                      </Message>
                    )}
                    <BookFormEnh onSave={handleSaveBook} />
                  </>
                ) : (
                  // Show a message or placeholder when no book is selected
                  <div className="bw-empty-selection-message">
                    <Header as="h3">Book Details</Header>
                    <p>Select a book from the list to view or edit its details.</p>
                  </div>
                )}
              </>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Delete Literary Group Confirmation Modal */}
      <Modal open={deleteLitGroupModalOpen} size="small">
        <Modal.Header>Delete Literary Group</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to delete this literary group? This will also delete all books and characters associated with it. This action cannot be undone.</p>
          {litGroupToDelete && (
            <Message warning>
              <Message.Header>You are about to delete:</Message.Header>
              <p><strong>{litGroupToDelete.name}</strong> ({litGroupToDelete.type || 'N/A'})</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={confirmDeleteLitGroup}>
            Delete
          </Button>
          <Button onClick={() => setDeleteLitGroupModalOpen(false)}>
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Book Confirmation Modal */}
      <Modal open={deleteBookModalOpen} size="small">
        <Modal.Header>Delete Book</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to delete this book? This will also delete all characters associated with it. This action cannot be undone.</p>
          {bookToDelete && (
            <Message warning>
              <Message.Header>You are about to delete:</Message.Header>
              <p><strong>{bookToDelete.title || bookToDelete.name}</strong></p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={confirmDeleteBook}>
            Delete
          </Button>
          <Button onClick={() => setDeleteBookModalOpen(false)}>
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    </ManagementHeader>
  );
};

export default BookManagementEnh;
